<template>
  <el-card>
    <div slot="header" class="toolbar">
      <span>积分任务</span>
      <el-button v-if="$hasPermission('mall:point-task:save')" type="primary" size="mini" @click="openCreate">新增任务</el-button>
    </div>

    <div class="search-bar search-wrap">
      <el-input v-model.trim="query.taskName" placeholder="任务名称" clearable style="width: 220px" @keyup.enter.native="handleSearch" />
      <el-select v-model="query.status" clearable placeholder="状态" style="width: 140px">
        <el-option :value="1" label="启用" />
        <el-option :value="0" label="禁用" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="reset">重置</el-button>
    </div>

    <el-table :data="list" border style="width: 100%">
      <el-table-column prop="taskName" label="任务名称" min-width="160" />
      <el-table-column prop="taskCode" label="任务编码" min-width="190">
        <template slot-scope="{ row }">{{ taskCodeText(row.taskCode) }}</template>
      </el-table-column>
      <el-table-column prop="cronExpression" label="cron表达式" width="180" />
      <el-table-column prop="status" label="状态" width="90" align="center">
        <template slot-scope="{ row }">{{ row.status === 1 ? '启用' : '禁用' }}</template>
      </el-table-column>
      <el-table-column prop="lastRunTime" label="最近执行时间" width="170" />
      <el-table-column prop="nextRunTime" label="下次执行时间" width="170" />
      <el-table-column prop="lastRunResult" label="最近执行结果" min-width="220" show-overflow-tooltip />
      <el-table-column label="操作" width="300" fixed="right">
        <template slot-scope="{ row }">
          <el-button v-if="$hasPermission('mall:point-task:update')" size="mini" @click="openEdit(row)">编辑</el-button>
          <el-button
            v-if="$hasPermission('mall:point-task:update')"
            :type="row.status === 1 ? 'warning' : 'success'"
            size="mini"
            @click="toggleStatus(row)"
          >
            {{ row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button v-if="$hasPermission('mall:point-task:execute')" type="primary" size="mini" @click="execute(row)">手动执行</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="query.page"
        :page-size="query.limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog :title="form.id ? '编辑积分任务' : '新增积分任务'" :visible.sync="dialogVisible" width="580px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="任务类型" prop="taskCode">
          <el-select v-model="form.taskCode" :disabled="!!form.id" style="width: 100%" @change="fillTaskName">
            <el-option label="活动积分过期处理" value="activity_point_expire" />
            <el-option label="冻结消费积分解冻" value="frozen_consume_unfreeze" />
          </el-select>
        </el-form-item>
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model.trim="form.taskName" />
        </el-form-item>
        <el-form-item label="cron表达式" prop="cronExpression">
          <el-input v-model.trim="form.cronExpression" placeholder="例如：0 5 0 * * ?" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button v-if="$hasPermission(form.id ? 'mall:point-task:update' : 'mall:point-task:save')" type="primary" @click="save">保存</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../../utils/http'

const CRON_FIELD_SEGMENTS = 6
const CRON_SPECIAL_PATTERN = /^[0-9A-Z*/,?\-LW#]+$/i

function defaultQuery () {
  return { page: 1, limit: 10, taskName: '', status: null }
}

function defaultForm () {
  return { id: null, taskCode: '', taskName: '', cronExpression: '0 5 0 * * ?', status: 1 }
}

export default {
  data () {
    const validateCronExpression = (rule, value, callback) => {
      const cron = String(value || '').trim()
      if (!cron) {
        callback(new Error('请输入cron表达式'))
        return
      }
      const parts = cron.split(/\s+/)
      if (parts.length !== CRON_FIELD_SEGMENTS) {
        callback(new Error('cron表达式需为6段，例如：0 5 0 * * ?'))
        return
      }
      const valid = parts.every(part => part && CRON_SPECIAL_PATTERN.test(part))
      if (!valid) {
        callback(new Error('cron表达式格式不正确'))
        return
      }
      callback()
    }
    return {
      query: defaultQuery(),
      total: 0,
      list: [],
      dialogVisible: false,
      form: defaultForm(),
      rules: {
        taskCode: [{ required: true, message: '请选择任务类型', trigger: 'change' }],
        taskName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        cronExpression: [{ required: true, validator: validateCronExpression, trigger: 'blur' }]
      }
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      const { data } = await http.get('/mall/point-task/list', { params: this.query })
      const pageData = data.data || {}
      this.list = pageData.list || []
      this.total = Number(pageData.total || 0)
    },
    handleSearch () {
      this.query.page = 1
      this.load()
    },
    handlePageChange (page) {
      this.query.page = page
      this.load()
    },
    handleSizeChange (limit) {
      this.query.limit = limit
      this.query.page = 1
      this.load()
    },
    reset () {
      this.query = defaultQuery()
      this.load()
    },
    openCreate () {
      this.form = defaultForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    async openEdit (row) {
      const { data } = await http.get(`/mall/point-task/info/${row.id}`)
      this.form = { ...defaultForm(), ...(data.data || row) }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    toggleStatus (row) {
      const nextStatus = row.status === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '启用' : '禁用'
      this.$confirm(`确定${actionText}任务“${row.taskName}”吗？`, '提示', { type: 'warning' }).then(async () => {
        const { data } = await http.post('/mall/point-task/status', {
          id: row.id,
          status: nextStatus
        })
        if (data.code === 0) {
          this.$message.success(`积分任务${actionText}成功`)
          this.load()
        }
      })
    },
    fillTaskName () {
      if (!this.form.taskName) {
        this.form.taskName = this.taskCodeText(this.form.taskCode)
      }
      if (this.form.taskCode === 'frozen_consume_unfreeze') {
        this.form.cronExpression = '0 10 0 * * ?'
      }
    },
    async validateCronWithServer () {
      const cronExpression = String(this.form.cronExpression || '').trim()
      const { data } = await http.get('/mall/point-task/validate-cron', {
        params: { cronExpression }
      })
      if (data.code !== 0 || !(data.data && data.data.valid)) {
        throw new Error(data.msg || 'cron表达式不正确')
      }
    },
    save () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        try {
          await this.validateCronWithServer()
          const url = this.form.id ? '/mall/point-task/update' : '/mall/point-task/save'
          const { data } = await http.post(url, this.form)
          if (data.code === 0) {
            this.$message.success(this.form.id ? '积分任务保存成功' : '积分任务新增成功')
            this.dialogVisible = false
            this.load()
          }
        } catch (error) {
          this.$message.error(error && error.message ? error.message : 'cron表达式不正确')
        }
      })
    },
    execute (row) {
      this.$confirm(`确定立即执行“${row.taskName}”吗？`, '提示', { type: 'warning' }).then(async () => {
        const { data } = await http.post(`/mall/point-task/execute/${row.id}`)
        if (data.code === 0) {
          const result = data.data || {}
          this.$message.success(result.summary || '积分任务执行完成')
          this.load()
        }
      })
    },
    taskCodeText (value) {
      const map = {
        activity_point_expire: '活动积分过期处理',
        frozen_consume_unfreeze: '冻结消费积分解冻'
      }
      return map[value] || value || '-'
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.search-wrap {
  flex-wrap: wrap;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
